import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { KnowledgeBaseInterface, KnowledgeSource, NamespaceInterface } from '@vendasta/embeddings';
import { RpcError } from '@vendasta/protos/fetch-transport';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import {
  CAN_SHARE_TOKEN,
  EDITING_ENABLED_TOKEN,
  KNOWLEDGE_GRANTS_ENABLED_TOKEN,
} from '../../tokens';
import { KnowledgeBaseDetailSourcesTableComponent } from '../knowledge-base-detail-sources-table/knowledge-base-detail-sources-table.component';
import { KnowledgeBaseGrantSdkService } from '../knowledge-base-grant-sdk.service';
import { KnowledgeBaseSharingDialogComponent } from '../knowledge-base-sharing-dialog/knowledge-base-sharing-dialog.component';
import {
  KnowledgeBaseSourcesDialogComponent,
  KnowledgeBaseSourcesDialogData,
  KnowledgeBaseSourcesResult,
  SourcesDialogKnowledgeSource,
} from '../knowledge-base-sources-dialog/knowledge-base-sources-dialog.component';
import { KnowledgeRowMapper } from '../knowledge-row.mapper';
import { namespaceFromRouteParam, namespacesEqual } from '../namespace.util';
import {
  KnowledgeBaseDetail,
  KnowledgeBaseGrant,
  KnowledgeBaseSharingDialogData,
  TableKnowledgeBase,
  TableKnowledgeSource,
} from '../types';

type LoadState = 'loading' | 'loaded' | 'not-found' | 'error';

/**
 * Routable container for the dedicated Knowledge Collection detail page. Given a `:kbId` route param it
 * gates on the `knowledge_grants_enabled` flag (KD1), fetches and assembles a {@link KnowledgeBaseDetail}
 * with explicit loading / not-found / error states, renders {@link KnowledgeBaseDetailSourcesTableComponent},
 * and wires its outputs to the real grant SDK + KB service with a refetch after every successful mutation.
 *
 * The story demo host (`knowledge-collection.stories.ts`) is reference UX only — its add/remove/describe
 * handlers mutate local state and persist nothing. This container persists through the real RPCs and is the
 * production owner of those actions. Description is read-only this slice (KD7); back navigation uses the
 * platform galaxy back button driven by route-data `previousPageUrl` (KD4).
 */
@Component({
  selector: 'ai-knowledge-knowledge-collection-detail-page',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    GalaxyPageModule,
    GalaxyEmptyStateModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    KnowledgeBaseDetailSourcesTableComponent,
  ],
  templateUrl: './knowledge-collection-detail-page.component.html',
  styleUrls: ['./knowledge-collection-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [KnowledgeRowMapper],
})
export class KnowledgeCollectionDetailPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly grants = inject(KnowledgeBaseGrantSdkService);
  private readonly mapper = inject(KnowledgeRowMapper);
  private readonly dialog = inject(MatDialog);
  // Sharing dialog opens via MatDialog (root overlay); pass this so the dialog's injector chains to this
  // component's (module) injector and can resolve KnowledgeBaseGrantSdkService + the per-app tokens.
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly snackbar = inject(SnackbarService);
  private readonly confirm = inject(OpenConfirmationModalService);
  private readonly translate = inject(TranslateService);
  private readonly canShare$ = inject(CAN_SHARE_TOKEN);
  private readonly editingEnabled$ = inject(EDITING_ENABLED_TOKEN);
  private readonly grantsEnabled$ = inject(KNOWLEDGE_GRANTS_ENABLED_TOKEN);

  readonly loadState = signal<LoadState>('loading');
  /** In-page busy overlay while a mutation + refetch runs, distinct from the full-page initial loading state. */
  readonly isBusy = signal(false);
  readonly detail = signal<KnowledgeBaseDetail | null>(null);
  readonly canShare = signal(false);
  readonly editingEnabled = signal(false);

  /** Deterministic back target (the Collections list); supplied per-app via route `data` (KD2/KD4). */
  previousPageUrl?: string;
  previousPageTitle?: string;

  private kbId = '';
  /** The KB's owning namespace, threaded from the entry point via the `ownerNs` query param. Undefined ⇒ falls back to
   * the caller's own namespace (the KB is owned by the current surface). */
  private ownerNs: NamespaceInterface | undefined;
  /** Whether knowledge grants are enabled (resolved in ngOnInit). Drives the cross-namespace opt-in when finding the KB. */
  private grantsOn = false;
  /** The fetched KB's own namespace — passed to mutation RPCs so they key off the right namespace. */
  private kbNamespace: NamespaceInterface | null = null;

  async ngOnInit(): Promise<void> {
    this.previousPageUrl = this.route.snapshot.data['previousPageUrl'];
    this.previousPageTitle = this.route.snapshot.data['previousPageTitle'];
    this.kbId = this.route.snapshot.paramMap.get('kbId') ?? '';
    // Owning namespace of the KB, carried from the collections list (query param) so the detail page can fetch the KB
    // directly via GetKnowledgeBase. Absent ⇒ the KB is owned by the current surface (caller namespace).
    this.ownerNs = namespaceFromRouteParam(this.route.snapshot.queryParamMap.get('ownerNs'));

    // KD1 self-gate: there is no route-level flag guard today, so the container redirects to the list
    // route when the flag is off rather than rendering a page that shouldn't be reachable.
    this.grantsOn = (await firstValueFrom(this.grantsEnabled$).catch(() => false)) ?? false;
    if (!this.grantsOn) {
      await this.router.navigateByUrl(this.previousPageUrl ?? '/');
      return;
    }

    this.canShare.set((await firstValueFrom(this.canShare$).catch(() => false)) ?? false);
    this.editingEnabled.set((await firstValueFrom(this.editingEnabled$).catch(() => false)) ?? false);

    await this.load(true);
  }

  retry(): void {
    void this.load(true);
  }

  /** Back-to-list action for the not-found state; `navigateByUrl` honors the `?tab=collections` query string. */
  back(): void {
    if (this.previousPageUrl) void this.router.navigateByUrl(this.previousPageUrl);
  }

  /**
   * Fetch the KB, its member sources, and its shares, and assemble the detail.
   * `initial` drives the full-page loading / not-found / error shell; a reload (initial=false) leaves the
   * loaded page in place and reports failure via snackbar so the page never flickers to an error shell.
   */
  private async load(initial: boolean): Promise<boolean> {
    if (initial) this.loadState.set('loading');
    try {
      const callerNs = this.knowledgeService.buildNamespaceInterface();
      // The caller namespace to send on grant-aware reads (undefined on admin surfaces, which are owned-only).
      const grantCallerNs = this.knowledgeService.callerNamespaceForGrants();
      // Fetch the KB directly from its owning namespace via GetKnowledgeBase — threaded from the collections list as
      // ownerNs, falling back to the caller's own namespace for an owned collection. Grant-aware (caller namespace)
      // so a collection shared into this namespace resolves, and includeKnowledgeSourceKeys populates member keys off
      // the KB's OWN namespace (ListKnowledgeBases keys them off the request namespace, so shared KBs come back empty).
      const owningNs = this.ownerNs ?? callerNs;
      let kbIface: KnowledgeBaseInterface;
      try {
        kbIface = await this.knowledgeService.getKnowledgeBaseForCaller(this.kbId, owningNs, grantCallerNs, true);
      } catch (err) {
        if (isNotFound(err)) {
          this.detail.set(null);
          this.loadState.set('not-found');
          return true;
        }
        throw err;
      }
      const kbRow = await this.mapper.toKbRow(kbIface, callerNs);
      // Fetch all member sources in one grant-aware batch, each keyed to its own owning namespace, so cross-namespace
      // (shared) sources resolve to their name/config instead of coming back empty (DALE-1416) — in a single
      // round-trip rather than one Get per source. Members of an owned KB resolve in the caller's own namespace.
      const memberKeys = (kbIface.knowledgeSourceKeys ?? [])
        .filter((key) => !!key.knowledgeSourceId)
        .map((key) => ({
          knowledgeSourceId: key.knowledgeSourceId,
          namespace: key.namespace ?? kbIface.namespace ?? callerNs,
        }));
      const sources = await this.knowledgeService.getMultiKnowledgeSourcesForCaller(memberKeys, grantCallerNs);
      const memberSources = await Promise.all(sources.map((source) => this.mapper.toKsRow(source, callerNs)));
      // The Shared-with card lists caller-issued grants (KD8); skip the fetch entirely when sharing is off.
      const shares = this.canShare() ? await this.grants.listGrantsForKB(this.kbId) : [];

      this.kbNamespace = kbIface.namespace ?? callerNs;
      this.detail.set({ kb: kbRow, sources: memberSources, shares });
      this.loadState.set('loaded');
      return true;
    } catch (err) {
      console.error('[knowledge-management] failed to load knowledge collection detail', err);
      if (initial) this.loadState.set('error');
      return false;
    }
  }

  /** Refetch after a mutation, showing the in-page busy overlay rather than the full-page loading shell. */
  private async reload(): Promise<boolean> {
    this.isBusy.set(true);
    try {
      return await this.load(false);
    } finally {
      this.isBusy.set(false);
    }
  }

  onShare(detail: KnowledgeBaseDetail): void {
    const data: KnowledgeBaseSharingDialogData = {
      kbId: detail.kb.id,
      kbName: detail.kb.name,
      // Add-only: the Shared-with card already lists current shares with inline revoke.
      hideCurrentShares: true,
    };
    this.dialog
      .open(KnowledgeBaseSharingDialogComponent, { data, width: '720px', maxWidth: '92vw', autoFocus: false, viewContainerRef: this.viewContainerRef })
      .afterClosed()
      .subscribe(() => void this.reload());
  }

  async onAddSource(detail: KnowledgeBaseDetail): Promise<void> {
    const callerNs = this.knowledgeService.buildNamespaceInterface();
    const kbNamespace = this.kbNamespace ?? callerNs;
    // Add candidates are the caller's own sources — you can only add sources you own, and this action is only available
    // for an owned collection. Fetched here (lazily, on open) rather than on every page load; surface a failure the way
    // the other async handlers do rather than swallowing the rejected promise.
    let ownedSources: KnowledgeSource[];
    try {
      ownedSources = await this.knowledgeService.listAllKnowledgeSources();
    } catch (err) {
      console.error('[knowledge-management] onAddSource failed to load sources', err);
      this.snackbar.openErrorSnack(this.translate.instant('KNOWLEDGE_SHARING.DETAIL.ADD_SOURCE_LOAD_ERROR'));
      return;
    }
    const availableSources: SourcesDialogKnowledgeSource[] = ownedSources
      .filter((s) => !!s.id && namespacesEqual(s.namespace ?? callerNs, callerNs))
      .map((s) => ({
        id: s.id ?? '',
        kind: this.mapper.ksKindFromConfig(s),
        name: s.name ?? '',
        subtitle: this.knowledgeService.getKnowledgePreview(s),
      }));
    const data: KnowledgeBaseSourcesDialogData = {
      kbId: detail.kb.id,
      kbName: detail.kb.name,
      kbDescription: detail.kb.description,
      managedBy: undefined,
      currentSources: detail.sources.map((s) => ({ id: s.id, kind: s.kind, name: s.name, subtitle: s.subtitle })),
      availableSources,
      // The merged dialog awaits onApply and only closes on success — surfacing errors and keeping the
      // selection on failure (mirrors KnowledgeBaseTableComponent.openSourcesDialog).
      onApply: async (result: KnowledgeBaseSourcesResult) => {
        await this.knowledgeService.updateKnowledgeBaseSources(detail.kb.id, kbNamespace, result.sourceIds);
      },
    };
    this.dialog
      .open(KnowledgeBaseSourcesDialogComponent, { data, width: '640px', maxWidth: '92vw', autoFocus: false })
      .afterClosed()
      .subscribe((result?: KnowledgeBaseSourcesResult) => {
        if (result) void this.reload();
      });
  }

  /**
   * Per-row "Remove from collection". Confirms first, then detaches the membership (the source itself is
   * untouched, so it stays on the Sources tab). Confirmed update model: no optimistic removal — refetch on
   * success, error snackbar on failure with the row left in place.
   */
  async onRemoveSource({ kb, source }: { kb: TableKnowledgeBase; source: TableKnowledgeSource }): Promise<void> {
    const confirmed = await firstValueFrom(
      this.confirm.openModal({
        type: 'warn',
        title: this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_TITLE'),
        message: this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_MESSAGE', {
          source: source.name,
          kb: kb.name,
        }),
        confirmButtonText: this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_CONFIRM'),
      }),
    );
    if (!confirmed) return;

    const remaining = (this.detail()?.kb.sourceIds ?? []).filter((id) => id !== source.id);
    const kbNamespace = this.kbNamespace ?? this.knowledgeService.buildNamespaceInterface();
    this.isBusy.set(true);
    try {
      await this.knowledgeService.updateKnowledgeBaseSources(kb.id, kbNamespace, remaining);
      await this.load(false);
      this.snackbar.openSuccessSnack(
        this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_SUCCESS', { source: source.name, kb: kb.name }),
      );
    } catch (err) {
      console.error('[knowledge-management] removeSource failed', err);
      this.snackbar.openErrorSnack(this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_ERROR'));
    } finally {
      this.isBusy.set(false);
    }
  }

  /**
   * Inline revoke from the Shared-with card. On `{ ok: false, dependents }` the grant is still active and
   * cleanup needs a follow-up action that isn't available yet — surface an honest blocked message rather
   * than implying auto-cleanup. On success, refetch the shares.
   */
  async onRevokeShare(grant: KnowledgeBaseGrant): Promise<void> {
    this.isBusy.set(true);
    try {
      const result = await this.grants.revokeGrant(this.kbId, grant.grantId);
      if (!('dependents' in result)) {
        await this.load(false);
        return;
      }
      this.snackbar.openErrorSnack(
        this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REVOKE_BLOCKED', { target: grant.target.displayName }),
      );
    } catch (err) {
      console.error('[knowledge-management] revokeShare failed', err);
      this.snackbar.openErrorSnack(this.translate.instant('KNOWLEDGE_SHARING.SHARE.REVOKE_ERROR'));
    } finally {
      this.isBusy.set(false);
    }
  }
}

/**
 * True when an RPC error is a NotFound (HTTP 404) — the KB doesn't exist or isn't visible to the caller. The `$` RPCs
 * (e.g. getKnowledgeBase$) go through Angular's HttpClient, so errors arrive as HttpErrorResponse; the Promise RPCs
 * throw RpcError. Handle both so detection doesn't hinge on the transport (mirrors isGrantHasDependentsError in
 * delete-knowledge-base.component.ts).
 */
function isNotFound(err: unknown): boolean {
  if (err instanceof HttpErrorResponse) return err.status === 404;
  if (err instanceof RpcError) return err.status === 404;
  return false;
}
